// useDataTableCellEditActivation.ts
import type { DataTableRowClickEvent } from 'primevue';
import type { ComputedRef, Ref } from 'vue';

import type { ColumnConfig, SelectionIntent } from '@/components/datatable/dataTableTypes';

type GetRowId = (row: any) => string | number | null;

type Deps = {
  /** Visible column configuration in the current table state (ordering, hidden columns applied). */
  visibleColumns: ComputedRef<ColumnConfig[]>;

  /** Stable row identity function. Must return unique id per row. */
  getRowId: GetRowId;

  /**
   * Active edit-cell state owned by the DataTable component.
   * This composable writes to it when a click should activate an editable cell.
   */
  activeEditCell: Ref<{ rowId: string | number; field: string } | null>;

  /**
   * Anchor id for range selection behaviors.
   * When activating a cell, we set the anchor to the clicked row.
   */
  selectionAnchorRowId: Ref<string | number | null>;

  /**
   * Centralized selection strategy dispatcher (see useDataTableSelection).
   * For edit activation we typically use 'ensure' to keep multi-selection stable.
   */
  applySelectionIntent: (row: any, intent: SelectionIntent) => void;

  // from useDataTableTechnicalColumns
  /**
   * Returns the <td> index (within the rendered row) for the click event,
   * including technical columns (checkbox, reorder handle, expander).
   * Returns -1 if the click was not inside a body cell.
   */
  getBodyCellIndexFromEvent: (e: MouseEvent) => number;

  /**
   * Converts a "body cell index" (including technical columns) into an index
   * within `visibleColumns`.
   */
  toVisibleColumnIndex: (bodyCellIndex: number) => number;
};

/**
 * Edit activation controller for DataTable cells.
 *
 * Responsibilities:
 * - Map a click location (MouseEvent) to the corresponding *visible* column.
 * - Validate whether the target column is editable.
 * - If editable: set `activeEditCell` (rowId + column field) to enter edit mode.
 * - If not editable: clear `activeEditCell` to ensure editing is not stuck.
 * - Ensure the clicked row is selected (using SelectionIntent 'ensure') so that:
 *   - single selection behaves naturally
 *   - multi-selection is preserved (does not get replaced implicitly)
 *
 * Notes:
 * - Technical columns (checkbox/expander/reorder) are handled outside of this composable.
 *   This composable assumes it is called only when a click should be interpreted as
 *   "try to activate an editor".
 */
export function useDataTableCellEditActivation(deps: Deps) {
  const {
    visibleColumns,
    getRowId,
    activeEditCell,
    selectionAnchorRowId,
    applySelectionIntent,
    getBodyCellIndexFromEvent,
    toVisibleColumnIndex,
  } = deps;

  /**
   * Activates editing based on a clicked row and the original MouseEvent.
   *
   * Returns:
   * - true  -> the click was handled as an (attempted) edit activation
   * - false -> the click could not be mapped to a body cell (no activation happened)
   *
   * Side effects:
   * - Updates `activeEditCell` (set or cleared)
   * - Updates `selectionAnchorRowId`
   * - Ensures the row is selected
   */
  function activateFromRowClick(row: any, originalEvent: MouseEvent): boolean {
    const bodyCellIndex = getBodyCellIndexFromEvent(originalEvent);
    if (bodyCellIndex < 0) return false;

    const visibleIndex = toVisibleColumnIndex(bodyCellIndex);
    const col = visibleColumns.value[visibleIndex];
    const rowId = getRowId(row);

    // Non-editable or invalid target: end any active editing state.
    if (!col || !col.editable || rowId == null) {
      activeEditCell.value = null;
      return true; // handled: we intentionally cleared
    }

    // Establish anchor for range selection navigation and ensure selection contains this row.
    selectionAnchorRowId.value = rowId;
    applySelectionIntent(row, 'ensure');

    // Activate the editor for this cell.
    activeEditCell.value = { rowId, field: col.field };
    return true;
  }

  /**
   * Convenience wrapper for PrimeVue DataTable row click events.
   * Extracts the original MouseEvent and delegates to `activateFromRowClick`.
   */
  function activateFromRowClickEvent(e: DataTableRowClickEvent): boolean {
    const original = e.originalEvent as MouseEvent;
    return activateFromRowClick(e.data, original);
  }

  return { activateFromRowClick, activateFromRowClickEvent };
}
